import { useCallback, useMemo, useState } from "react";
import { DEFAULT_PINS, MAX_PINS, type NavGroup, type NavItem } from "./nav";
import { moved } from "@/lib/useDragReorder";

const STORAGE_KEY = "nav-pins";

const read = (): string[] | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : null;
    return Array.isArray(list) && list.every((k) => typeof k === "string") ? list : null;
  } catch {
    return null;
  }
};

/**
 * Which modules sit on the sidebar itself (the rest are under More). Remembered per browser; keys the person can no
 * longer see (or that no longer exist) are ignored, and an empty result falls back to the default set.
 */
export function usePinnedNav(groups: NavGroup[]) {
  const [stored, setStored] = useState<string[] | null>(read);

  const all = useMemo(() => groups.flatMap((g) => g.items.map((item) => ({ item, group: g }))), [groups]);

  // The pinned modules in the order the person put them (drag in "Customise the sidebar")
  const orderedKeys = useMemo(() => {
    const visible = new Set(all.map((x) => x.item.key));
    const own = Array.from(new Set(stored ?? [])).filter((k) => visible.has(k)).slice(0, MAX_PINS);
    const keys = stored && own.length ? own : DEFAULT_PINS.filter((k) => visible.has(k));
    // Never leave the sidebar empty for someone who can see only other modules
    return keys.length ? keys : all.slice(0, 6).map((x) => x.item.key);
  }, [stored, all]);
  const pinnedKeys = useMemo(() => new Set(orderedKeys), [orderedKeys]);

  const save = useCallback((keys: string[] | null) => {
    setStored(keys);
    try {
      if (keys) localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* not remembered */
    }
  }, []);

  /** In the person's order, each with its group (the sidebar draws a divider where the group changes) */
  const pinned = useMemo(() => {
    const byKey = new Map(all.map((x) => [x.item.key, x]));
    return orderedKeys.map((k) => byKey.get(k)!).filter(Boolean);
  }, [all, orderedKeys]);
  const unpinned = useMemo(() => all.filter((x) => !pinnedKeys.has(x.item.key)).map((x) => x.item), [all, pinnedKeys]);

  const isPinned = useCallback((key: string) => pinnedKeys.has(key), [pinnedKeys]);

  /** false when it can't be pinned because the limit is reached */
  const toggle = useCallback(
    (key: string): boolean => {
      if (pinnedKeys.has(key)) {
        // Keep at least one module on the sidebar
        if (orderedKeys.length > 1) save(orderedKeys.filter((k) => k !== key));
        return true;
      }
      if (orderedKeys.length >= MAX_PINS) return false;
      save([...orderedKeys, key]);
      return true;
    },
    [pinnedKeys, orderedKeys, save],
  );

  /** Moves a pinned module to a new place on the sidebar */
  const move = useCallback((from: number, to: number) => save(moved(orderedKeys, from, to)), [orderedKeys, save]);

  const reset = useCallback(() => save(null), [save]);

  return { move, all: all as { item: NavItem; group: NavGroup }[], pinned: pinned as { item: NavItem; group: NavGroup }[], unpinned, isPinned, toggle, reset, count: pinnedKeys.size, max: MAX_PINS };
}

export type PinnedNav = ReturnType<typeof usePinnedNav>;
